import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { createPortal } from "react-dom";
import { Gift, X, Star, Sparkles, Heart } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context";
import { useFabsCollapsed } from "../lib/fabTidy";
import { useDraggableFab } from "../lib/useDraggableFab";
import { runEffect } from "../lib/founderParticles";
import { playEffectSound, playGiftOpen } from "../lib/founderFx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One full-screen effect canvas (used when a kid opens an effect/combo gift).
function GiftFx({ effect }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const stop = runEffect(ref.current, effect, 4200);
    return () => { try { stop(); } catch (_) { /* noop */ } };
  }, [effect]);
  return (
    <div className="fixed inset-0 pointer-events-none z-[2147483200]" data-testid="gift-effects-layer">
      <canvas ref={ref} className="absolute inset-0" style={{ width: "100vw", height: "100vh" }} data-effect={effect} />
    </div>
  );
}

// The kid Command Center: shows gifts an admin (Isaiah/David/Vanessa) sent to
// THIS explorer. They open a gift to claim it — stars are added to their
// account, effects/combos rain down the screen. No admin powers here.
export default function GiftInbox() {
  const { user, token } = useApp();
  const collapsed = useFabsCollapsed();
  const drag = useDraggableFab("iw_fab_pos_gift", useLocation().pathname.startsWith("/learn/tutor") ? 236 : 168, { defaultSide: "right" });
  const [gifts, setGifts] = useState([]);
  const [pending, setPending] = useState(0);
  const [streak, setStreak] = useState({ count: 0, badge: null });
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem("iw_gift_hidden") === "1"; } catch { return false; } });
  const hide = () => { try { localStorage.setItem("iw_gift_hidden", "1"); } catch (_) {} setHidden(true); setOpen(false); };
  const show = () => { try { localStorage.removeItem("iw_gift_hidden"); } catch (_) {} setHidden(false); };
  const [fxList, setFxList] = useState([]);
  const [busy, setBusy] = useState(null);
  const notifiedRef = useRef(false);

  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : null;

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/founder/gifts/inbox`, { headers: { Authorization: `Bearer ${token}` } });
      const gs = r.data?.gifts || [];
      const pc = r.data?.pending_count || 0;
      setGifts(gs);
      setPending(pc);
      setStreak(r.data?.streak || { count: 0, badge: null });
      const rewards = r.data?.rewards || [];
      if (rewards.length) {
        const total = rewards.reduce((s, x) => s + (x.stars || 0), 0);
        const top = Math.max(...rewards.map((x) => x.milestone));
        toast.success(`🔥 ${top}-day gift streak! +${total.toLocaleString()} bonus stars!`, { duration: 6000 });
        playGiftOpen("star-rain");
        playEffects(["star-rain", "confetti"]);
      }
      // "You got a gift!" — fire ONCE per app open when something is waiting.
      if (!notifiedRef.current && pc > 0) {
        notifiedRef.current = true;
        toast("🎁 You got a gift!", {
          description: pc > 1 ? `${pc} waiting for you` : "Tap to open it.",
          action: { label: "Open", onClick: () => setOpen(true) },
        });
      }
    } catch (_) { /* silent */ }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    load();
    const iv = setInterval(load, 45000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [token, load]);

  // Open the gift panel when the header notification bell asks us to
  // (tapping a "You got a gift!" bell notification). Refresh first so a
  // freshly-sent gift is present before the panel opens.
  useEffect(() => {
    const openFromBell = () => { load(); setOpen(true); };
    window.addEventListener("iw-open-gift-inbox", openFromBell);
    return () => window.removeEventListener("iw-open-gift-inbox", openFromBell);
  }, [load]);

  const playEffects = (effects) => {
    (effects || []).forEach((e, i) => {
      const key = `${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`;
      setTimeout(() => {
        setFxList((cur) => [...cur, { effect: e, key }]);
        playEffectSound();
        setTimeout(() => setFxList((cur) => cur.filter((f) => f.key !== key)), 5400);
      }, i * 350);
    });
  };

  const claim = async (g) => {
    if (!auth) return;
    setBusy(g.id);
    try {
      const r = await axios.post(`${API}/founder/gift/${g.id}/claim`, {}, auth);
      const gift = r.data?.gift || g;
      playGiftOpen(gift.kind === "stars" ? "star-rain" : gift.payload?.effects?.[0]);
      if (gift.kind === "stars") {
        const amt = r.data?.granted_stars ?? gift.payload?.amount ?? 0;
        toast.success(`+${Number(amt).toLocaleString()} stars added to your account! ⭐`);
      } else {
        playEffects(gift.payload?.effects);
        setOpen(false);
      }
      await load();
    } catch (_) {
      toast.error("Could not open that gift. Try again.");
    } finally {
      setBusy(null);
    }
  };

  const replay = (g) => { playEffects(g.payload?.effects); setOpen(false); };

  const sayThanks = async (g) => {
    if (!auth) return;
    try {
      await axios.post(`${API}/founder/gift/${g.id}/thank`, {}, auth);
      toast.success("Thanks sent! 💛");
      await load();
    } catch (_) {
      toast.error("Could not send thanks.");
    }
  };

  if (!user || !token) return null;
  if (gifts.length === 0) return null;      // no clutter when nothing was sent
  if (collapsed) return fxList.map((f) => <GiftFx key={f.key} effect={f.effect} />);

  // Tucked off-screen: leave a small peek tab on the left edge to bring it back.
  if (hidden) {
    return createPortal(
      <>
        {fxList.map((f) => <GiftFx key={f.key} effect={f.effect} />)}
        <button
          onClick={show}
          className="fixed left-0 bottom-[150px] z-[70] flex items-center justify-center active:scale-95 transition-transform"
          style={{ width: 26, height: 44, borderTopRightRadius: 9999, borderBottomRightRadius: 9999, border: "1px solid rgba(255,255,255,0.25)", borderLeft: "none", background: "linear-gradient(135deg,#c026d3,#f59e0b)", boxShadow: "0 4px 14px rgba(0,0,0,0.4)" }}
          data-testid="gift-restore"
          aria-label="Show gifts"
          title="Show my gifts"
        >
          <Gift size={15} className="text-white" />
          {pending > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 border border-white/40 text-white text-[9px] font-black flex items-center justify-center">{pending}</span>}
        </button>
      </>,
      document.body,
    );
  }

  return createPortal(
    <>
      {fxList.map((f) => <GiftFx key={f.key} effect={f.effect} />)}

      {/* Gift helper — draggable, and dismissible (X) like the other helpers */}
      <div className="fixed z-[70]" style={drag.style} {...drag.bind}>
        <button
          onClick={() => { if (drag.shouldSwallowClick()) return; setOpen((o) => !o); }}
          className="relative w-11 h-11 rounded-full flex items-center justify-center shadow-lg border-2 border-white/25 bg-gradient-to-br from-fuchsia-500 to-amber-400 active:scale-95 transition-transform"
          data-testid="gift-inbox-fab"
          aria-label="My gifts"
          title="My gifts — drag to move"
        >
          <Gift size={20} className="text-white" />
          {pending > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 border border-white/40 text-white text-[10px] font-black flex items-center justify-center" data-testid="gift-inbox-badge">
              {pending}
            </span>
          )}
        </button>
        <button
          onClick={hide}
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-900 border border-white/60 flex items-center justify-center shadow hover:bg-black active:scale-95 transition-all"
          data-testid="gift-inbox-hide"
          aria-label="Hide gifts"
          title="Hide (tap the edge tab to bring it back)"
        >
          <X size={10} className="text-white/90" />
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[2147483300] flex items-end sm:items-center justify-center" data-testid="gift-inbox-panel">
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md mx-auto rounded-t-3xl sm:rounded-3xl border-2 border-white/15 bg-slate-900/98 shadow-2xl overflow-hidden max-h-[78vh] flex flex-col">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-amber-300" />
                <p className="text-white font-fredoka font-bold text-base">My Command Center</p>
              </div>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center" data-testid="gift-inbox-close" aria-label="Close">
                <X size={16} className="text-white/70" />
              </button>
            </div>
            <div className="overflow-y-auto overscroll-contain p-3 space-y-2">
              {streak.count > 0 && (
                <div className="flex items-center gap-2 rounded-2xl px-3 py-2 border border-amber-400/30 bg-gradient-to-r from-amber-500/15 to-fuchsia-500/10" data-testid="gift-streak-banner">
                  <span className="text-xl">🔥</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm" data-testid="gift-streak-count">{streak.count} day{streak.count === 1 ? "" : "s"} in a row!</p>
                    <p className="text-white/50 text-[11px]">Come back tomorrow to keep your streak going.</p>
                  </div>
                  {streak.badge && (
                    <span className="px-2 py-1 rounded-full bg-amber-400 text-slate-900 text-[10px] font-black shrink-0" data-testid="gift-streak-badge">
                      {streak.badge >= 14 ? "🏆 Legend" : streak.badge >= 7 ? "🌟 Superstar" : "✨ On Fire"}
                    </span>
                  )}
                </div>
              )}
              {gifts.map((g) => {
                const isPending = g.status === "pending";
                const isThanks = g.kind === "thanks";
                return (
                  <div key={g.id} className={`rounded-2xl p-3 border ${isPending ? "border-amber-400/40 bg-amber-500/[0.08]" : "border-white/10 bg-white/[0.03]"}`} data-testid={`gift-item-${g.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500/30 to-amber-400/30 flex items-center justify-center flex-shrink-0">
                        {isThanks ? <Heart size={20} className="text-rose-300" /> : g.kind === "stars" ? <Star size={20} className="text-amber-300" /> : <Sparkles size={20} className="text-fuchsia-300" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-bold text-sm truncate">{g.payload?.label || "A gift"}</p>
                        <p className="text-white/50 text-[11px] truncate">from {g.sender_name || "an admin"}{g.message ? ` · "${g.message}"` : ""}</p>
                      </div>
                      {isPending ? (
                        <button onClick={() => claim(g)} disabled={busy === g.id}
                          className="px-3 py-1.5 rounded-xl bg-amber-400 text-slate-900 font-bold text-xs disabled:opacity-50 active:scale-95 transition-transform"
                          data-testid={`gift-open-${g.id}`}>
                          {busy === g.id ? "…" : isThanks ? "Got it" : "Open"}
                        </button>
                      ) : !isThanks && g.kind !== "stars" ? (
                        <button onClick={() => replay(g)}
                          className="px-3 py-1.5 rounded-xl bg-white/10 text-white/80 font-bold text-xs active:scale-95 transition-transform"
                          data-testid={`gift-replay-${g.id}`}>
                          Play again
                        </button>
                      ) : !isThanks ? (
                        <span className="text-emerald-300 text-[11px] font-bold" data-testid={`gift-claimed-${g.id}`}>Claimed ✓</span>
                      ) : null}
                    </div>
                    {/* Say thanks back — only on real gifts you've opened */}
                    {!isThanks && !g.thanked && !isPending && (
                      <button onClick={() => sayThanks(g)} className="mt-2 text-[11px] font-bold text-fuchsia-300 hover:text-fuchsia-200 active:scale-95 transition-transform" data-testid={`gift-thanks-${g.id}`}>
                        💛 Say thanks
                      </button>
                    )}
                    {!isThanks && g.thanked && <p className="mt-2 text-[11px] text-white/40">Thanks sent 💛</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
